import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Box, DraggableGrid, useBiAction, useDevice } from 'seagull';
import { ChevronLeft } from 'seagull/icons/arrows/ChevronLeft';
import { ChevronRight } from 'seagull/icons/arrows/ChevronRight';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import styles from './MetricsCardStrip.module.scss';
export const GetMetricsCardStripTID = (parentTestId)=>getTestIds(parentTestId);
const MARKER_ATTR = 'data-metric-card-item';
const STRIP_GAP_PX = 16;
const FALLBACK_WIDGET_WIDTH_PX = 240;
const ItemRenderer = (param)=>{
    let { render, isDragActive, dragHandle, onRemove } = param;
    return /*#__PURE__*/ _jsx("div", {
        [MARKER_ATTR]: true,
        className: styles.metricCardItemMarker,
        children: render({
            isEditMode: true,
            isDragActive,
            dragHandle,
            onRemove
        })
    });
};
export const MetricsCardStrip = (param)=>{
    let { items, isEditMode, onReorder, onRemove, testId } = param;
    const { MetricsCardStripTestIds } = useMemo(()=>GetMetricsCardStripTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('components.invoiceMetricsSection');
    const { isMobileDevice } = useDevice();
    const logBiAction = useBiAction();
    const rootRef = useRef(null);
    const previousItemCountRef = useRef(items.length);
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);
    const updateFadeVisibility = useCallback(()=>{
        const node = rootRef.current;
        if (node == null) return;
        const { scrollLeft, scrollWidth, clientWidth } = node;
        setShowLeftFade(scrollLeft > 0);
        setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);
    }, []);
    useLayoutEffect(()=>{
        updateFadeVisibility();
    }, [
        items.length,
        isEditMode,
        updateFadeVisibility
    ]);
    useEffect(()=>{
        const node = rootRef.current;
        if (node == null) return undefined;
        const onScroll = ()=>updateFadeVisibility();
        node.addEventListener('scroll', onScroll, {
            passive: true
        });
        const resizeObserver = new ResizeObserver(updateFadeVisibility);
        resizeObserver.observe(node);
        return ()=>{
            node.removeEventListener('scroll', onScroll);
            resizeObserver.disconnect();
        };
    }, [
        updateFadeVisibility
    ]);
    useEffect(()=>{
        const previousCount = previousItemCountRef.current;
        previousItemCountRef.current = items.length;
        const isWidgetAddedInEditMode = isEditMode === true && previousCount > 0 && items.length > previousCount;
        if (isWidgetAddedInEditMode && rootRef.current != null) {
            rootRef.current.scrollTo({
                left: rootRef.current.scrollWidth,
                behavior: 'smooth'
            });
        }
    }, [
        items.length,
        isEditMode
    ]);
    const scrollByOneWidget = useCallback((direction)=>{
        var _ref;
        const node = rootRef.current;
        if (node == null) return;
        const firstItem = node.querySelector("[".concat(MARKER_ATTR, "]"));
        const widgetWidth = (_ref = firstItem === null || firstItem === void 0 ? void 0 : firstItem.getBoundingClientRect().width) !== null && _ref !== void 0 ? _ref : FALLBACK_WIDGET_WIDTH_PX;
        const step = widgetWidth + STRIP_GAP_PX;
        node.scrollBy({
            left: direction === 'right' ? step : -step,
            behavior: 'smooth'
        });
        logBiAction({
            entityType: 'icon_button',
            entityName: direction === 'right' ? 'scroll_metrics_strip_right' : 'scroll_metrics_strip_left',
            actionType: 'click'
        });
    }, [
        logBiAction
    ]);
    const renderOverlays = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.fade, styles.fadeLeft, {
                        [styles.fadeVisible]: showLeftFade
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.fade, styles.fadeRight, {
                        [styles.fadeVisible]: showRightFade
                    })
                }),
                !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: cn(styles.scrollButton, styles.scrollButtonLeft, {
                                [styles.scrollButtonVisible]: showLeftFade
                            }),
                            "data-testid": MetricsCardStripTestIds.ScrollLeftButton(),
                            "aria-label": t.scrollLeftAriaLabel(),
                            onClick: ()=>scrollByOneWidget('left'),
                            children: /*#__PURE__*/ _jsx(ChevronLeft, {
                                size: 20
                            })
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: cn(styles.scrollButton, styles.scrollButtonRight, {
                                [styles.scrollButtonVisible]: showRightFade
                            }),
                            "data-testid": MetricsCardStripTestIds.ScrollRightButton(),
                            "aria-label": t.scrollRightAriaLabel(),
                            onClick: ()=>scrollByOneWidget('right'),
                            children: /*#__PURE__*/ _jsx(ChevronRight, {
                                size: 20
                            })
                        })
                    ]
                })
            ]
        });
    if (isEditMode) {
        const hasMultipleItems = items.length > 1;
        if (!hasMultipleItems) {
            return /*#__PURE__*/ _jsxs("div", {
                className: styles.container,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        ref: rootRef,
                        className: styles.root,
                        "data-testid": MetricsCardStripTestIds.Root(),
                        children: /*#__PURE__*/ _jsx(Box, {
                            className: styles.staticStrip,
                            children: items.map((item)=>/*#__PURE__*/ _jsx(Box, {
                                    className: styles.staticItem,
                                    testId: MetricsCardStripTestIds.Item(item.id),
                                    [MARKER_ATTR]: true,
                                    children: item.render({
                                        isEditMode: true,
                                        onRemove: onRemove ? ()=>onRemove(item.id) : undefined
                                    })
                                }, item.id))
                        })
                    }),
                    renderOverlays()
                ]
            });
        }
        const draggableItems = items.map((item)=>_object_spread_props(_object_spread({}, item), {
                onRemove: onRemove ? ()=>onRemove(item.id) : undefined
            }));
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.container,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    ref: rootRef,
                    className: styles.root,
                    "data-testid": MetricsCardStripTestIds.Root(),
                    children: /*#__PURE__*/ _jsx(DraggableGrid, {
                        bi: {
                            name: 'reorder_invoice_metric_card'
                        },
                        className: styles.draggableGridOverride,
                        itemClassName: styles.draggableGridItem,
                        items: draggableItems,
                        itemComponent: ItemRenderer,
                        layout: {
                            rows: 1
                        },
                        gap: 16,
                        dragMode: "withGhost",
                        itemHandleVisibility: "always",
                        itemDragActivation: "handle",
                        itemHandlePosition: "inside",
                        onReorder: (reordered)=>onReorder === null || onReorder === void 0 ? void 0 : onReorder(reordered.map((item)=>item.id))
                    })
                }),
                renderOverlays()
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.container,
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: rootRef,
                className: styles.root,
                "data-testid": MetricsCardStripTestIds.Root(),
                children: /*#__PURE__*/ _jsx(Box, {
                    className: styles.staticStrip,
                    children: items.map((item)=>/*#__PURE__*/ _jsx(Box, {
                            className: styles.staticItem,
                            testId: MetricsCardStripTestIds.Item(item.id),
                            [MARKER_ATTR]: true,
                            children: item.render({
                                isEditMode: false
                            })
                        }, item.id))
                })
            }),
            renderOverlays()
        ]
    });
};
